import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import * as Icons from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEVICE_BRANDS = {
  smartphone: ['Apple', 'Samsung', 'Google', 'Xiaomi', 'Huawei', 'OnePlus', 'Sony', 'Andere'],
  laptop: ['Apple', 'Lenovo', 'Dell', 'HP', 'Asus', 'Acer', 'Microsoft', 'Andere']
};

const STORAGE_OPTIONS = {
  smartphone: ['64 GB', '128 GB', '256 GB', '512 GB', '1 TB'],
  laptop: ['256 GB', '512 GB', '1 TB', '2 TB']
};

const RAM_OPTIONS = ['8 GB', '16 GB', '32 GB', '64 GB'];

const CONDITIONS = [
  {
    value: 'excellent',
    label: 'Wie neu',
    description: 'Kaum sichtbare Gebrauchsspuren. Technisch einwandfrei.',
    multiplier: 1.0,
    icon: Icons.Sparkles
  },
  {
    value: 'good',
    label: 'Sehr gut',
    description: 'Leichte Gebrauchsspuren möglich, technisch vollständig funktional.',
    multiplier: 0.85,
    icon: Icons.CheckCircle2
  },
  {
    value: 'fair',
    label: 'Gut',
    description: 'Deutlich sichtbare Nutzungsspuren, technisch funktionsfähig.',
    multiplier: 0.7,
    icon: Icons.Check
  },
  {
    value: 'poor',
    label: 'Stark genutzt / Defekt',
    description: 'Defekte Funktionen oder starke Beschädigungen.',
    multiplier: 0.4,
    icon: Icons.AlertCircle
  }
];

const BATTERY_HEALTH_OPTIONS = [
  { value: 100, label: '100%', multiplier: 1.0 },
  { value: 95, label: '90-99%', multiplier: 0.95 },
  { value: 85, label: '80-89%', multiplier: 0.85 },
  { value: 70, label: 'Unter 80%', multiplier: 0.7 }
];

export const DevicePriceWizardV2 = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    deviceType: '',
    brand: '',
    model: '',
    deviceId: '',
    storage: '',
    ram: '',
    condition: '',
    batteryHealth: 100,
    accessories: [],
    photos: [], // array of {file, url}
    name: '',
    email: '',
    phone: '',
    payoutMethod: 'bank',
    additionalInfo: '',
    calculatedPrice: null,
    priceLow: null,
    priceHigh: null
  });
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [animatedPrice, setAnimatedPrice] = useState(0);

  // Step layout
  // Laptop (9):    1 type | 2 brand | 3 model | 4 specs    | 5 condition | 6 accessories | 7 photos | 8 offer | 9 contact
  // Smartphone(10):1 type | 2 brand | 3 model | 4 storage  | 5 condition | 6 battery     | 7 access | 8 photos| 9 offer | 10 contact
  const totalSteps = formData.deviceType === 'smartphone' ? 10 : 9;
  const hasDeviceType = Boolean(formData.deviceType);
  const offsetSP = formData.deviceType === 'smartphone' ? 1 : 0; // smartphone adds the battery step

  const isStep = {
    type: step === 1,
    brand: step === 2,
    model: step === 3,
    specs: step === 4,
    condition: step === 5,
    battery: step === 6 && formData.deviceType === 'smartphone',
    accessories: step === 6 + offsetSP, // laptop:6, smartphone:7
    photos: step === 7 + offsetSP,      // laptop:7, smartphone:8
    offer: step === 8 + offsetSP,       // laptop:8, smartphone:9
    contact: step === totalSteps
  };

  // Unique model names for the picked manufacturer + deviceType
  const uniqueModels = Array.from(new Set(devices.map(d => d.model)));
  // Storage options available for the picked model
  const storagesForModel = devices
    .filter(d => d.model === formData.model)
    .map(d => d.storage);
  const uniqueStorages = Array.from(new Set(storagesForModel));

  useEffect(() => {
    if (formData.brand && formData.deviceType) {
      fetchDevices();
    }
  }, [formData.brand, formData.deviceType]);

  const fetchDevices = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/devices/models?manufacturer=${encodeURIComponent(formData.brand)}&category=${formData.deviceType}`
      );
      const data = await response.json();
      if (data.success) {
        setDevices(data.devices || []);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      setDevices([]);
    }
  };

  // When the user picks a model + storage, resolve the concrete device.id
  const resolveDeviceId = (model, storage) => {
    const match = devices.find(d => d.model === model && d.storage === storage);
    return match ? match.id : '';
  };

  const hasAccessories = () =>
    formData.accessories.includes('Ladegerät') || formData.accessories.includes('Kabel');
  const hasOriginalBox = () => formData.accessories.includes('Originalverpackung');

  const calculatePrice = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/pricing/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId: formData.deviceId,
          condition: formData.condition,
          batteryHealth: formData.deviceType === 'smartphone' ? formData.batteryHealth : 100,
          hasAccessories: hasAccessories(),
          hasOriginalBox: hasOriginalBox()
        })
      });
      const data = await response.json();
      if (data.success) {
        const base = Math.round(data.calculatedPrice);
        const low = Math.max(0, Math.round(base * 0.9));
        const high = Math.round(base * 1.1);
        setFormData((prev) => ({ ...prev, calculatedPrice: base, priceLow: low, priceHigh: high }));
        return base;
      }
    } catch (error) {
      toast.error('Fehler bei der Preisberechnung');
    } finally {
      setLoading(false);
    }
    return null;
  };

  // Count-up animation when offer step is reached
  useEffect(() => {
    if (!isStep.offer || !formData.calculatedPrice) return;
    const target = formData.calculatedPrice;
    const duration = 1200;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimatedPrice(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [isStep.offer, formData.calculatedPrice]);

  // Photo upload to backend
  const onPhotosChange = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - formData.photos.length);
    // Add placeholders immediately for UI feedback
    const placeholders = files.map((f) => ({
      file: f,
      url: URL.createObjectURL(f),
      name: f.name,
      uploading: true,
      uploadedUrl: null,
      error: null
    }));
    const startIndex = formData.photos.length;
    setFormData((prev) => ({ ...prev, photos: [...prev.photos, ...placeholders] }));

    // Upload each in parallel
    await Promise.all(files.map(async (file, i) => {
      const idx = startIndex + i;
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${BACKEND_URL}/api/leads/c2b/photos/upload`, {
          method: 'POST',
          body: fd
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setFormData((prev) => {
            const next = [...prev.photos];
            if (next[idx]) {
              next[idx] = { ...next[idx], uploading: false, uploadedUrl: data.url };
            }
            return { ...prev, photos: next };
          });
        } else {
          throw new Error(data.detail || 'Upload fehlgeschlagen');
        }
      } catch (err) {
        setFormData((prev) => {
          const next = [...prev.photos];
          if (next[idx]) {
            next[idx] = { ...next[idx], uploading: false, error: err.message || 'Fehler' };
          }
          return { ...prev, photos: next };
        });
        toast.error(`Foto-Upload fehlgeschlagen: ${file.name}`);
      }
    }));
    // reset input so same file can be picked again
    if (e.target) e.target.value = '';
  };

  const removePhoto = async (idx) => {
    const photo = formData.photos[idx];
    // Optimistic UI update
    const next = [...formData.photos];
    next.splice(idx, 1);
    if (photo?.url) URL.revokeObjectURL(photo.url);
    setFormData({ ...formData, photos: next });

    // Fire-and-forget cleanup of the orphan file on the server
    if (photo?.uploadedUrl) {
      const filename = photo.uploadedUrl.split('/').pop();
      try {
        await fetch(`${BACKEND_URL}/api/uploads/c2b_photos/${filename}`, { method: 'DELETE' });
      } catch (e) {
        // non-blocking; orphan cleanup will handle it later
      }
    }
  };

  const photosUploading = formData.photos.some((p) => p.uploading);

  const submitLead = async () => {
    setSubmitting(true);
    try {
      const price = formData.calculatedPrice || (await calculatePrice());

      const extraInfo = [
        formData.additionalInfo,
        formData.payoutMethod ? `Bevorzugte Auszahlung: ${formData.payoutMethod}` : null
      ].filter(Boolean).join(' | ');

      const photoUrls = formData.photos
        .filter((p) => p.uploadedUrl)
        .map((p) => p.uploadedUrl);

      const response = await fetch(`${BACKEND_URL}/api/leads/c2b`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          deviceId: formData.deviceId,
          deviceCategory: formData.deviceType,
          deviceManufacturer: formData.brand,
          deviceModel: formData.model,
          storage: formData.storage,
          condition: formData.condition,
          batteryHealth: formData.deviceType === 'smartphone' ? formData.batteryHealth : 100,
          hasAccessories: hasAccessories(),
          hasOriginalBox: hasOriginalBox(),
          calculatedPrice: price || 0,
          priceAccepted: true,
          additionalInfo: extraInfo || null,
          photoUrls: photoUrls
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Anfrage erfolgreich gesendet! Sie erhalten eine Bestätigungs-Email.');
        // Reset form
        setStep(1);
        setAnimatedPrice(0);
        // revoke object URLs
        formData.photos.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
        setFormData({
          deviceType: '',
          brand: '',
          model: '',
          deviceId: '',
          storage: '',
          ram: '',
          condition: '',
          batteryHealth: 100,
          accessories: [],
          photos: [],
          name: '',
          email: '',
          phone: '',
          payoutMethod: 'bank',
          additionalInfo: '',
          calculatedPrice: null,
          priceLow: null,
          priceHigh: null
        });
        setDevices([]);
      } else {
        toast.error('Fehler beim Senden der Anfrage');
      }
    } catch (error) {
      toast.error('Fehler beim Senden der Anfrage');
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const toggleAccessory = (accessory) => {
    if (formData.accessories.includes(accessory)) {
      setFormData({
        ...formData,
        accessories: formData.accessories.filter(a => a !== accessory)
      });
    } else {
      setFormData({
        ...formData,
        accessories: [...formData.accessories, accessory]
      });
    }
  };

  const slideVariants = {
    enter: { opacity: 0, x: 50 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[#143e6e]">
            {hasDeviceType ? `Schritt ${step} von ${totalSteps}` : 'Starten Sie hier'}
          </span>
          <span className="text-sm text-gray-500">
            {hasDeviceType ? `${Math.round((step / totalSteps) * 100)}%` : ''}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <motion.div
            className="bg-gradient-to-r from-[#1f7db6] to-[#143e6e] h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: hasDeviceType ? `${(step / totalSteps) * 100}%` : '0%' }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.3 }}
        >
          <Card className="shadow-lg">
            <CardContent className="p-8">
              {/* Step 1: Device Type */}
              {isStep.type && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-[#143e6e] mb-2">
                      Was möchten Sie verkaufen?
                    </h2>
                    <p className="text-gray-600">Wählen Sie Ihr Gerät aus</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <motion.button
                      whileHover={{ scale: 1.02, y: -4 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setFormData({ ...formData, deviceType: 'laptop' });
                        setTimeout(nextStep, 300);
                      }}
                      className={`p-8 border-2 rounded-xl transition-all ${
                        formData.deviceType === 'laptop'
                          ? 'border-[#1f7db6] bg-[#1f7db6]/5'
                          : 'border-gray-200 hover:border-[#1f7db6]/50'
                      }`}
                    >
                      <Icons.Laptop className="mx-auto text-[#1f7db6] mb-4" size={64} />
                      <h3 className="text-xl font-bold text-[#143e6e] mb-2">Laptop verkaufen</h3>
                      <p className="text-sm text-gray-600">MacBook, ThinkPad, Dell & mehr</p>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02, y: -4 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setFormData({ ...formData, deviceType: 'smartphone' });
                        setTimeout(nextStep, 300);
                      }}
                      className={`p-8 border-2 rounded-xl transition-all ${
                        formData.deviceType === 'smartphone'
                          ? 'border-[#1f7db6] bg-[#1f7db6]/5'
                          : 'border-gray-200 hover:border-[#1f7db6]/50'
                      }`}
                    >
                      <Icons.Smartphone className="mx-auto text-[#1f7db6] mb-4" size={64} />
                      <h3 className="text-xl font-bold text-[#143e6e] mb-2">Smartphone verkaufen</h3>
                      <p className="text-sm text-gray-600">iPhone, Samsung, Google & mehr</p>
                    </motion.button>
                  </div>
                </div>
              )}

              {/* Step 2: Brand */}
              {isStep.brand && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-[#143e6e] mb-2">Hersteller wählen</h2>
                    <p className="text-gray-600">Welche Marke ist Ihr {formData.deviceType === 'laptop' ? 'Laptop' : 'Smartphone'}?</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {DEVICE_BRANDS[formData.deviceType].map((brand) => (
                      <motion.button
                        key={brand}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setFormData({ ...formData, brand });
                          setTimeout(nextStep, 300);
                        }}
                        className={`p-6 border-2 rounded-xl text-center transition-all ${
                          formData.brand === brand
                            ? 'border-[#1f7db6] bg-[#1f7db6]/5'
                            : 'border-gray-200 hover:border-[#1f7db6]/50'
                        }`}
                      >
                        <p className="font-semibold text-[#143e6e]">{brand}</p>
                      </motion.button>
                    ))}
                  </div>
                  <Button onClick={prevStep} variant="outline" className="w-full mt-4">
                    Zurück
                  </Button>
                </div>
              )}

              {/* Step 3: Model Selection */}
              {isStep.model && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-[#143e6e] mb-2">Modell auswählen</h2>
                    <p className="text-gray-600">Welches {formData.brand} Modell haben Sie?</p>
                  </div>
                  <div className="space-y-4">
                    <Label>Modell</Label>
                    <Select
                      value={formData.model}
                      onValueChange={(v) => setFormData({ ...formData, model: v, storage: '', deviceId: '' })}
                    >
                      <SelectTrigger className="h-12" data-testid="wizard-model-select">
                        <SelectValue placeholder="Modell auswählen..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {uniqueModels.length > 0 ? (
                          uniqueModels.map((modelName) => (
                            <SelectItem key={modelName} value={modelName}>{modelName}</SelectItem>
                          ))
                        ) : (
                          <SelectItem value="__none__" disabled>Keine Modelle verfügbar</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={prevStep} variant="outline" className="flex-1" data-testid="wizard-back-btn">
                      Zurück
                    </Button>
                    <Button 
                      onClick={nextStep} 
                      disabled={!formData.model}
                      className="flex-1 bg-[#1f7db6] hover:bg-[#143e6e]"
                      data-testid="wizard-next-btn"
                    >
                      Weiter
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Storage / Tech Specs */}
              {isStep.specs && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-[#143e6e] mb-2">
                      {formData.deviceType === 'smartphone' ? 'Speicherkapazität' : 'Technische Daten'}
                    </h2>
                    <p className="text-gray-600">
                      {formData.deviceType === 'smartphone' 
                        ? 'Wie viel Speicher hat Ihr Gerät?' 
                        : 'Geben Sie die technischen Spezifikationen an'}
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Speicherkapazität</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                        {(uniqueStorages.length > 0 ? uniqueStorages : STORAGE_OPTIONS[formData.deviceType]).map((storage) => (
                          <button
                            key={storage}
                            data-testid={`wizard-storage-${storage}`}
                            onClick={() => {
                              const newDeviceId = resolveDeviceId(formData.model, storage);
                              setFormData({ ...formData, storage, deviceId: newDeviceId });
                            }}
                            className={`p-4 border-2 rounded-lg font-medium transition-all ${
                              formData.storage === storage
                                ? 'border-[#1f7db6] bg-[#1f7db6]/5 text-[#1f7db6]'
                                : 'border-gray-200 hover:border-[#1f7db6]/50'
                            }`}
                          >
                            {storage}
                          </button>
                        ))}
                      </div>
                    </div>

                    {formData.deviceType === 'laptop' && (
                      <div>
                        <Label>Arbeitsspeicher (RAM)</Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                          {RAM_OPTIONS.map((ram) => (
                            <button
                              key={ram}
                              data-testid={`wizard-ram-${ram}`}
                              onClick={() => setFormData({ ...formData, ram })}
                              className={`p-4 border-2 rounded-lg font-medium transition-all ${
                                formData.ram === ram
                                  ? 'border-[#1f7db6] bg-[#1f7db6]/5 text-[#1f7db6]'
                                  : 'border-gray-200 hover:border-[#1f7db6]/50'
                              }`}
                            >
                              {ram}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Button onClick={prevStep} variant="outline" className="flex-1">
                      Zurück
                    </Button>
                    <Button 
                      onClick={nextStep} 
                      disabled={!formData.storage || !formData.deviceId || (formData.deviceType === 'laptop' && !formData.ram)}
                      className="flex-1 bg-[#1f7db6] hover:bg-[#143e6e]"
                      data-testid="wizard-next-btn-step4"
                    >
                      Weiter
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 5: Condition */}
              {isStep.condition && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-[#143e6e] mb-2">Zustand des Geräts</h2>
                    <p className="text-gray-600">Wie würden Sie den Zustand einschätzen?</p>
                  </div>
                  <div className="space-y-4">
                    {CONDITIONS.map((condition) => {
                      const Icon = condition.icon;
                      return (
                        <motion.button
                          key={condition.value}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setFormData({ ...formData, condition: condition.value });
                            setTimeout(nextStep, 300);
                          }}
                          className={`w-full p-6 border-2 rounded-xl text-left transition-all ${
                            formData.condition === condition.value
                              ? 'border-[#1f7db6] bg-[#1f7db6]/5'
                              : 'border-gray-200 hover:border-[#1f7db6]/50'
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <Icon className="text-[#1f7db6] flex-shrink-0" size={32} />
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-[#143e6e] mb-1">
                                {condition.label}
                              </h3>
                              <p className="text-sm text-gray-600">{condition.description}</p>
                            </div>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                  <Button onClick={prevStep} variant="outline" className="w-full">
                    Zurück
                  </Button>
                </div>
              )}

              {/* Step 6: Battery Health (Smartphones only) */}
              {isStep.battery && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-[#143e6e] mb-2">Batteriezustand</h2>
                    <p className="text-gray-600">Wie ist die Akku-Gesundheit?</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Tipp: Prüfbar in den iPhone-Einstellungen unter Batterie → Batteriezustand
                    </p>
                  </div>
                  <div className="space-y-4">
                    {BATTERY_HEALTH_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setFormData({ ...formData, batteryHealth: option.value })}
                        className={`w-full p-6 border-2 rounded-xl text-center font-medium transition-all ${
                          formData.batteryHealth === option.value
                            ? 'border-[#1f7db6] bg-[#1f7db6]/5 text-[#1f7db6]'
                            : 'border-gray-200 hover:border-[#1f7db6]/50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={prevStep} variant="outline" className="flex-1">
                      Zurück
                    </Button>
                    <Button onClick={nextStep} className="flex-1 bg-[#1f7db6] hover:bg-[#143e6e]">
                      Weiter
                    </Button>
                  </div>
                </div>
              )}

              {/* Step: Accessories */}
              {isStep.accessories && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-[#143e6e] mb-2">Zubehör</h2>
                    <p className="text-gray-600">Was ist im Lieferumfang enthalten?</p>
                  </div>
                  <div className="space-y-3">
                    {['Originalverpackung', 'Ladegerät', 'Kabel', 'Rechnung'].map((accessory) => (
                      <button
                        key={accessory}
                        data-testid={`wizard-accessory-${accessory}`}
                        onClick={() => toggleAccessory(accessory)}
                        className={`w-full p-4 border-2 rounded-lg flex items-center gap-3 transition-all ${
                          formData.accessories.includes(accessory)
                            ? 'border-[#1f7db6] bg-[#1f7db6]/5'
                            : 'border-gray-200 hover:border-[#1f7db6]/50'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                          formData.accessories.includes(accessory)
                            ? 'border-[#1f7db6] bg-[#1f7db6]'
                            : 'border-gray-300'
                        }`}>
                          {formData.accessories.includes(accessory) && (
                            <Icons.Check className="text-white" size={16} />
                          )}
                        </div>
                        <span className="font-medium text-[#143e6e]">{accessory}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={prevStep} variant="outline" className="flex-1">
                      Zurück
                    </Button>
                    <Button onClick={nextStep} className="flex-1 bg-[#1f7db6] hover:bg-[#143e6e]">
                      Weiter
                    </Button>
                  </div>
                </div>
              )}

              {/* Step: Photos (optional) */}
              {isStep.photos && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-[#143e6e] mb-2">Fotos hinzufügen</h2>
                    <p className="text-gray-600">
                      Optional, aber empfohlen — Fotos beschleunigen die Bewertung.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {formData.photos.map((p, i) => (
                      <div key={i} className="relative aspect-square rounded-xl overflow-hidden border-2 border-[#1f7db6]/30 group" data-testid={`wizard-photo-preview-${i}`}>
                        <img src={p.url} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                        {p.uploading && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <Icons.Loader2 className="animate-spin text-white" size={24} />
                          </div>
                        )}
                        {p.error && (
                          <div className="absolute inset-0 bg-red-500/70 flex items-center justify-center text-white text-xs font-medium px-2 text-center">
                            <span>Fehler</span>
                          </div>
                        )}
                        {p.uploadedUrl && !p.uploading && (
                          <div className="absolute top-2 left-2 bg-emerald-500 text-white rounded-full p-1">
                            <Icons.Check size={12} />
                          </div>
                        )}
                        <button
                          onClick={() => removePhoto(i)}
                          className="absolute top-2 right-2 bg-black/70 hover:bg-black/90 text-white rounded-full p-1.5 shadow-md transition z-10"
                          data-testid={`wizard-photo-remove-${i}`}
                          aria-label="Foto entfernen"
                        >
                          <Icons.X size={14} />
                        </button>
                      </div>
                    ))}
                    {formData.photos.length < 6 && (
                      <label
                        htmlFor="wizard-photo-input"
                        className="aspect-square rounded-xl border-2 border-dashed border-[#1f7db6]/40 hover:border-[#1f7db6] hover:bg-[#1f7db6]/5 flex flex-col items-center justify-center cursor-pointer transition"
                        data-testid="wizard-photo-add"
                      >
                        <Icons.Camera className="text-[#1f7db6] mb-2" size={28} />
                        <span className="text-xs font-medium text-[#143e6e]">Foto hinzufügen</span>
                        <span className="text-[10px] text-gray-500 mt-1">max. 6</span>
                        <input
                          id="wizard-photo-input"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={onPhotosChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>

                  <div className="bg-[#1f7db6]/5 border border-[#1f7db6]/20 rounded-lg p-4 flex gap-3">
                    <Icons.Info className="text-[#1f7db6] flex-shrink-0 mt-0.5" size={18} />
                    <p className="text-sm text-gray-700">
                      Tipp: Vorder- und Rückseite sowie Display-Zustand fotografieren — das hilft bei der finalen Bewertung.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button onClick={prevStep} variant="outline" className="flex-1" data-testid="wizard-photos-back">
                      Zurück
                    </Button>
                    <Button
                      onClick={async () => {
                        await calculatePrice();
                        nextStep();
                      }}
                      disabled={loading || photosUploading}
                      className="flex-1 bg-[#1f7db6] hover:bg-[#143e6e]"
                      data-testid="wizard-photos-next"
                    >
                      {loading ? (
                        <><Icons.Loader2 className="animate-spin mr-2" size={18} /> Berechne...</>
                      ) : photosUploading ? (
                        <><Icons.Loader2 className="animate-spin mr-2" size={18} /> Fotos werden hochgeladen...</>
                      ) : (
                        <>Sofortangebot anzeigen <Icons.Sparkles className="ml-2" size={16} /></>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step: Instant Offer */}
              {isStep.offer && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  <div className="text-center mb-2">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1, rotate: [0, -8, 8, 0] }}
                      transition={{ delay: 0.2, duration: 0.6 }}
                      className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#1f7db6] to-[#143e6e] shadow-lg mb-4"
                    >
                      <Icons.Sparkles className="text-white" size={28} />
                    </motion.div>
                    <h2 className="text-3xl md:text-4xl font-bold text-[#143e6e] mb-2">
                      Ihr geschätztes Sofortangebot
                    </h2>
                    <p className="text-gray-600">
                      Basierend auf Ihren Angaben — transparent und fair.
                    </p>
                  </div>

                  {/* Premium price card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="relative"
                  >
                    {/* glow */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-[#1f7db6] via-[#3da9e0] to-[#143e6e] rounded-3xl blur-2xl opacity-30 animate-pulse" />
                    <Card className="relative bg-gradient-to-br from-white via-[#f8fbff] to-[#eaf3fb] border-[#1f7db6]/30 shadow-2xl rounded-3xl overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#1f7db6]/20 to-transparent rounded-full blur-2xl" />
                      <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-[#143e6e]/15 to-transparent rounded-full blur-3xl" />
                      <CardContent className="relative p-8 md:p-12 text-center">
                        <p className="text-sm uppercase tracking-widest text-[#1f7db6] font-semibold mb-2">
                          Geschätzter Ankaufspreis
                        </p>

                        {loading || !formData.calculatedPrice ? (
                          <div className="flex items-center justify-center py-8">
                            <Icons.Loader2 className="animate-spin text-[#1f7db6]" size={40} />
                          </div>
                        ) : (
                          <>
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.5 }}
                              data-testid="wizard-offer-price"
                              className="text-6xl md:text-7xl font-extrabold bg-gradient-to-r from-[#1f7db6] to-[#143e6e] bg-clip-text text-transparent tabular-nums leading-none my-4"
                            >
                              {animatedPrice} €
                            </motion.div>
                            <motion.p
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 1.0 }}
                              className="text-lg text-gray-700"
                            >
                              Spanne: <span className="font-semibold text-[#143e6e]">
                                {formData.priceLow} € – {formData.priceHigh} €
                              </span>
                            </motion.p>
                          </>
                        )}

                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.2 }}
                          className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium border border-emerald-200"
                        >
                          <Icons.TrendingUp size={16} />
                          Ihr Gerät hat einen hohen Wiederverkaufswert.
                        </motion.div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Trust messaging */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4 }}
                    className="grid grid-cols-1 md:grid-cols-3 gap-3"
                  >
                    {[
                      { icon: Icons.ShieldCheck, text: 'Faire Bewertung nach technischer Prüfung' },
                      { icon: Icons.Truck, text: 'Kostenloser Versand mit Tracking' },
                      { icon: Icons.Banknote, text: 'Schnelle Auszahlung nach Eingang' }
                    ].map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div key={i} className="flex items-start gap-2 p-3 bg-white border border-gray-100 rounded-lg shadow-sm">
                          <Icon className="text-[#1f7db6] flex-shrink-0 mt-0.5" size={18} />
                          <span className="text-xs text-gray-700 leading-snug">{item.text}</span>
                        </div>
                      );
                    })}
                  </motion.div>

                  <p className="text-xs text-gray-500 text-center">
                    Hinweis: Die endgültige Bewertung erfolgt nach Eingang und technischer Prüfung Ihres Geräts.
                    {!hasAccessories() && !hasOriginalBox() && (
                      <> Mit vollständigem Zubehör kann sich der Ankaufspreis erhöhen.</>
                    )}
                  </p>

                  <div className="flex gap-3 pt-2">
                    <Button onClick={prevStep} variant="outline" className="flex-1" data-testid="wizard-offer-back">
                      Zurück
                    </Button>
                    <Button
                      onClick={nextStep}
                      className="flex-1 bg-gradient-to-r from-[#1f7db6] to-[#143e6e] hover:opacity-90 text-white"
                      data-testid="wizard-offer-accept"
                    >
                      Angebot annehmen <Icons.ArrowRight className="ml-2" size={16} />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Final Step: Contact Details */}
              {isStep.contact && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-[#143e6e] mb-2">
                      Wohin dürfen wir Ihr Angebot senden?
                    </h2>
                    <p className="text-gray-600">
                      Nur noch ein letzter Schritt — Ihre Daten sind sicher und DSGVO-konform.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        data-testid="wizard-contact-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Ihr Name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>E-Mail *</Label>
                      <Input
                        data-testid="wizard-contact-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="ihre.email@beispiel.de"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Telefon *</Label>
                      <Input
                        data-testid="wizard-contact-phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+49 123 456789"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Bevorzugte Auszahlungsmethode</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        {[
                          { value: 'bank', label: 'Banküberweisung', icon: Icons.Banknote },
                          { value: 'paypal', label: 'PayPal', icon: Icons.Wallet }
                        ].map((opt) => {
                          const Icon = opt.icon;
                          return (
                            <button
                              key={opt.value}
                              onClick={() => setFormData({ ...formData, payoutMethod: opt.value })}
                              data-testid={`wizard-payout-${opt.value}`}
                              className={`p-3 border-2 rounded-lg flex items-center gap-2 transition ${
                                formData.payoutMethod === opt.value
                                  ? 'border-[#1f7db6] bg-[#1f7db6]/5 text-[#1f7db6]'
                                  : 'border-gray-200 hover:border-[#1f7db6]/50 text-[#143e6e]'
                              }`}
                            >
                              <Icon size={18} />
                              <span className="text-sm font-medium">{opt.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <Label>Zusätzliche Informationen (optional)</Label>
                      <Textarea
                        value={formData.additionalInfo}
                        onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                        placeholder="Besondere Hinweise zu Ihrem Gerät..."
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Summary Preview */}
                  <Card className="bg-gradient-to-br from-[#1f7db6]/5 to-[#143e6e]/5 border-[#1f7db6]/20">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-[#143e6e] mb-4 flex items-center gap-2">
                        <Icons.FileText size={20} />
                        Zusammenfassung
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Gerät:</span>
                          <span className="font-medium">{formData.brand} {formData.model}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Speicher:</span>
                          <span className="font-medium">{formData.storage}</span>
                        </div>
                        {formData.ram && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">RAM:</span>
                            <span className="font-medium">{formData.ram}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Zustand:</span>
                          <span className="font-medium">
                            {CONDITIONS.find(c => c.value === formData.condition)?.label}
                          </span>
                        </div>
                        {formData.deviceType === 'smartphone' && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Akku:</span>
                            <span className="font-medium">
                              {BATTERY_HEALTH_OPTIONS.find(b => b.value === formData.batteryHealth)?.label}
                            </span>
                          </div>
                        )}
                        {formData.accessories.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Zubehör:</span>
                            <span className="font-medium">{formData.accessories.length} Artikel</span>
                          </div>
                        )}
                        {formData.photos.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Fotos:</span>
                            <span className="font-medium">{formData.photos.length} Bilder</span>
                          </div>
                        )}
                        {formData.calculatedPrice && (
                          <div className="flex justify-between pt-2 mt-2 border-t border-[#1f7db6]/20">
                            <span className="text-gray-700 font-semibold">Geschätzter Preis:</span>
                            <span className="font-bold text-[#1f7db6] text-lg">
                              {formData.priceLow} – {formData.priceHigh} €
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex gap-3">
                    <Button onClick={prevStep} variant="outline" className="flex-1" data-testid="wizard-contact-back">
                      Zurück
                    </Button>
                    <Button
                      onClick={submitLead}
                      disabled={!formData.name || !formData.email || !formData.phone || submitting}
                      className="flex-1 bg-gradient-to-r from-[#1f7db6] to-[#143e6e] hover:opacity-90"
                      data-testid="wizard-submit-btn"
                    >
                      {submitting ? (
                        <>
                          <Icons.Loader2 className="animate-spin mr-2" size={18} />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          <Icons.Send className="mr-2" size={18} />
                          Anfrage absenden
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};